import { Form, Formik } from "formik";
import * as yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { Button } from "@/core/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useSubmitTechFeedback } from "./query";

interface TechFeedbackFormValues {
  name: string;
  email: string;
  inquiry: string;
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  inquiry: yup
    .string()
    .required("Inquiry is required")
    .min(10, "Inquiry must be at least 10 characters")
    .max(1500, "Inquiry must be less than 1500 characters"),
});

const initialValues: TechFeedbackFormValues = {
  name: "",
  email: "",
  inquiry: "",
};

const TechFeedback = () => {
  const { mutate: submitFeedback, isPending } = useSubmitTechFeedback();

  const handleSubmit = (
    values: TechFeedbackFormValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    submitFeedback(values, {
      onSuccess: () => {
        toast.success("Thank you for your feedback!", {
          description: "We'll get back to you as soon as possible.",
        });
        resetForm();
      },
      onError: (error) => {
        toast.error("Failed to submit feedback", {
          description: error.errors?.join(", ") || "Please try again later.",
        });
      },
    });
  };

  return (
    <Stack fullWidth className="min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Tech Feedback</CardTitle>
          <CardDescription>
            Have a question or suggestion? We'd love to hear from you.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ getFieldProps, errors, touched }) => (
              <Form>
                <Stack gap="lg">
                  <Stack fullWidth gap="sm">
                    <Typography
                      variant={"subtitleW3"}
                      textColor={"interactive"}
                    >
                      Name
                    </Typography>
                    <FormInput
                      label="Name"
                      placeholder="Your name"
                      required
                      fullWidth
                      {...getFieldProps("name")}
                    />
                  </Stack>

                  <Stack fullWidth gap="sm">
                    <Typography
                      variant={"subtitleW3"}
                      textColor={"interactive"}
                    >
                      Work Email
                    </Typography>
                    <FormInput
                      label="Email"
                      type="email"
                      placeholder="work.email@example.com"
                      required
                      fullWidth
                      {...getFieldProps("email")}
                    />
                  </Stack>

                  <Stack gap="xs" fullWidth>
                    <Textarea
                      label="Inquiry"
                      placeholder="Tell us what's on your mind"
                      required
                      {...getFieldProps("inquiry")}
                    />
                    {touched.inquiry && errors.inquiry && (
                      <Typography variant="bodyW2" className="text-destructive">
                        {errors.inquiry}
                      </Typography>
                    )}
                  </Stack>

                  <Button type="submit" loading={isPending} fullWidth>
                    Submit Feedback
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default TechFeedback;
